import { useState, useEffect } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const defaultMessage = (order) => {
  const ref = order?.reference || "";
  return `Nous constatons qu'un solde de ${formatCurrency(order?.remaining || 0)} reste dû sur votre commande ${ref}, ` +
    `terminée depuis ${order?.days_since_completion || 0} jour(s). ` +
    "Nous vous remercions de bien vouloir procéder à son règlement dans les meilleurs délais.\n\n" +
    "Cordialement,\nL'équipe Bati Assistant";
};

export const PaymentReminderDialog = ({ open, onOpenChange, order, onSent }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && order) {
      setEmail("");
      setMessage(defaultMessage(order));
    }
  }, [open, order]);

  const handleSend = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Email client invalide");
      return;
    }
    try {
      setSending(true);
      const res = await api.sendPaymentReminder({
        order_id: order.order_id,
        email: email.trim(),
        custom_message: message,
      });
      toast.success(res.data.message);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'envoi"));
    } finally {
      setSending(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Envoyer une relance de paiement
          </DialogTitle>
          <DialogDescription>
            Commande <strong>{order.reference}</strong> — Client <strong>{order.client_name}</strong>
            <br />
            Reste dû : <strong className="text-amber-700">{formatCurrency(order.remaining)}</strong>
            {" · "}
            +{order.days_since_completion}j depuis la fin
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reminder-email">Email du client</Label>
            <Input
              id="reminder-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@exemple.fr"
              data-testid="reminder-email-input"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reminder-message">Message (modifiable)</Label>
            <Textarea
              id="reminder-message"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="reminder-message-input"
            />
            <p className="text-xs text-muted-foreground">
              Le message ci-dessus sera inclus dans l'email. Le récapitulatif (référence, HT, déjà payé, reste dû) est ajouté automatiquement.
            </p>
          </div>
          {order.last_reminder_at && (
            <p className="text-xs text-muted-foreground">
              Dernière relance envoyée le {new Date(order.last_reminder_at).toLocaleString("fr-FR")}
              {order.last_reminder_email ? ` à ${order.last_reminder_email}` : ""}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSend} disabled={sending || !email} data-testid="send-reminder-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Envoyer la relance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentReminderDialog;
